import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import CloneVoiceUpload from "./CloneVoiceUpload";
import CloneVoiceInformation from "./CloneVoiceInformation";
import { createVoiceClone, getVoiceCloneStats, VoiceCloneStats } from "../../../api/voiceClone";
import CloneVoiceStatusModal from "../../../components/modals/CloneVoiceStatusModal";
import styles from "./CloneVoice.module.css";

const CloneVoice: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [currentStep, setCurrentStep] = useState<"upload" | "information">("upload");
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [stats, setStats] = useState<VoiceCloneStats | null>(null);
    const [isLoadingStats, setIsLoadingStats] = useState(true);
    const [statusModal, setStatusModal] = useState<{
        isOpen: boolean;
        status: "waiting" | "success" | "error";
        title: string;
        subtitle: string;
    }>({
        isOpen: false,
        status: "waiting",
        title: "",
        subtitle: "",
    });

    // Fetch voice clone stats on component mount
    useEffect(() => {
        const fetchStats = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) return;

                const statsData = await getVoiceCloneStats(token);
                setStats(statsData);
            } catch (error) {
                console.error("Error fetching voice clone stats:", error);
            } finally {
                setIsLoadingStats(false);
            }
        };

        fetchStats();
    }, []);

    const handleUploadComplete = (files: File[]) => {
        // Check if user can create more voice clones
        if (stats && !stats.canCreateMore) {
            alert(t("voiceCloneLimitReached"));
            return;
        }

        setUploadedFiles(files);
        setCurrentStep("information");
    };

    const handleBack = () => {
        setCurrentStep("upload");
    };

    const handleSaveVoice = async (
        name: string,
        description: string,
        gender?: string,
        accent?: string,
        age?: string,
        language?: string,
    ) => {
        if (uploadedFiles.length === 0) {
            alert(t("uploadFailed"));
            return;
        }

        setIsSaving(true);
        setStatusModal({
            isOpen: true,
            status: "waiting",
            title: t("voiceCloningInProgress"),
            subtitle: t("voiceCloningInProgressSubtitle"),
        });

        try {
            const token = localStorage.getItem("token");
            if (!token) {
                alert(t("pleaseLogin"));
                setIsSaving(false);
                return;
            }

            setStatusModal({
                isOpen: true,
                status: "waiting",
                title: t("voiceCloningInProgress"),
                subtitle: t("voiceCloningInProgressSubtitle"),
            });

            const { uploadFileToS3Complete } = await import("../../../api/s3");
            const uploadPromises = uploadedFiles.map((file) =>
                uploadFileToS3Complete(
                    {
                        file: file,
                        bucket: "rewindstories",
                        isPublic: true,
                        isFolder: false,
                    },
                    token,
                ),
            );

            const uploadedUrls = await Promise.all(uploadPromises);

            if (!uploadedUrls.every((url) => url)) {
                alert(t("uploadFailed"));
                setIsSaving(false);
                return;
            }

            const response = await createVoiceClone({
                token,
                name,
                description,
                audioUrls: uploadedUrls as string[],
                gender,
                accent,
                age,
                language,
            });

            console.log("Voice clone created:", response);

            const verificationText = response.voiceClone.requiresVerification
                ? `\n${t("voiceCloneVerificationNotice")}`
                : "";
            setStatusModal({
                isOpen: true,
                status: "success",
                title: t("voiceCloneSuccessTitle"),
                subtitle: `${t("voiceCloneSuccessSubtitle", {
                    voiceId: response.voiceClone.externalVoiceId,
                })}${verificationText}`,
            });

            // Refresh stats after successful creation
            try {
                const updatedStats = await getVoiceCloneStats(token);
                setStats(updatedStats);
            } catch (error) {
                console.error("Error refreshing stats:", error);
            }

            // Reset form and go back to upload step
            setUploadedFiles([]);
            setCurrentStep("upload");
        } catch (error: any) {
            console.error("Error creating voice clone:", error);

            // Get error code and message
            const errorCode = error?.error || error?.details?.error;
            const errorMessage = error?.details?.message || error?.message || "";

            // Handle specific error cases
            if (errorCode === "voice-clone-limit-reached" || error?.details?.message === "voice-clone-limit-reached") {
                setStatusModal({
                    isOpen: true,
                    status: "error",
                    title: t("voiceCloneErrorTitle"),
                    subtitle: `${t("voiceCloneLimitReached")}\n${t("maximumVoiceClonesReached", {
                        limit: stats?.limit ?? 0,
                    })}`,
                });
            } else if (errorCode === "duplicated-files") {
                setStatusModal({
                    isOpen: true,
                    status: "error",
                    title: t("voiceCloneErrorTitle"),
                    subtitle: t("duplicatedFilesError"),
                });
            } else {
                setStatusModal({
                    isOpen: true,
                    status: "error",
                    title: t("voiceCloneErrorTitle"),
                    subtitle: errorMessage || t("voiceCloneErrorSubtitle"),
                });
            }

            // Refresh stats to ensure UI is up to date
            try {
                const token = localStorage.getItem("token");
                if (token) {
                    const updatedStats = await getVoiceCloneStats(token);
                    setStats(updatedStats);
                }
            } catch (statsError) {
                console.error("Error refreshing stats after error:", statsError);
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <div className={styles.cloneVoiceContainer}>
                <div className={styles.cloneVoiceHeader}>
                    <div className={styles.headerTop}>
                        <h2>{t("instantVoiceClone")}</h2>
                        {!isLoadingStats && stats && (
                            <div className={styles.voiceCloneStats}>
                                <span className={styles.statsLabel}>{t("voiceClonesRemaining")}:</span>
                                <span
                                    className={`${styles.statsValue} ${stats.remaining === 0 ? styles.limitReached : ""}`}
                                >
                                    {stats.remaining} / {stats.limit}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className={styles.stepsIndicator}>
                        <div
                            className={`${styles.step} ${currentStep === "upload" ? styles.activeStep : styles.completedStep}`}
                        >
                            <span className={styles.dot} />
                            <span className={styles.stepLabel}>{t("uploadAudio")}</span>
                        </div>
                        <div className={styles.stepLine}></div>
                        <div className={`${styles.step} ${currentStep === "information" ? styles.activeStep : ""}`}>
                            <span className={styles.dot} />
                            <span className={styles.stepLabel}>{t("voiceInformation")}</span>
                        </div>
                    </div>
                </div>

                {currentStep === "upload" && (
                    <>
                        {stats && !stats.canCreateMore && (
                            <div className={styles.limitReachedBanner}>
                                <p>{t("voiceCloneLimitReached")}</p>
                                <p>{t("maximumVoiceClonesReached", { limit: stats.limit })}</p>
                            </div>
                        )}
                        <CloneVoiceUpload
                            onUploadComplete={handleUploadComplete}
                            disabled={stats ? !stats.canCreateMore : false}
                        />
                    </>
                )}

                {currentStep === "information" && uploadedFiles.length > 0 && (
                    <CloneVoiceInformation
                        files={uploadedFiles}
                        onBack={handleBack}
                        onSave={handleSaveVoice}
                        isSaving={isSaving}
                    />
                )}
            </div>
            <CloneVoiceStatusModal
                isOpen={statusModal.isOpen}
                status={statusModal.status}
                title={statusModal.title}
                subtitle={statusModal.subtitle}
                onClose={() =>
                    setStatusModal((prev) => ({
                        ...prev,
                        isOpen: false,
                    }))
                }
            />
        </>
    );
};

export default CloneVoice;
